import {
  CustomColumnDefinitionDtoMetadataType,
  type CustomColumnDefinitionDto,
  type PartnerStructuredColumnDto,
} from '@artic/api-client';

import { isCustomColumnActive } from '../../custom-columns/column-visibility';
import type { PartnerContainer } from '../../products/types';
import { sortContainers } from '../sort-containers';

/**
 * Kind of grid column an Excel column can map onto. Drives how the importer
 * parses / validates the cell value and how the apply step addresses the field
 * on the batch-update payload.
 */
export type ImportTargetKind =
  | 'identity'
  | 'container'
  | 'text'
  | 'integer'
  | 'date'
  | 'number_with_unit'
  | 'metadata';

/**
 * One mappable destination in the planner grid. The Excel Import wizard offers
 * these as the choices for each spreadsheet column.
 */
export interface ImportTarget {
  /**
   * Stable target key, matching how the batch-update payload addresses the
   * field:
   *   - identity          → `work_order_name`
   *   - container         → the `container_key`
   *   - structured scalar → the `work_order_field`
   *   - metadata          → `metadata.<key>`
   */
  field: string;
  /** Human-readable label shown in the mapping dropdown (matches the grid header). */
  label: string;
  kind: ImportTargetKind;
  /** kind === 'container' — the container, for option resolution + applicability + product-type gating. */
  container?: PartnerContainer;
  /** kind === 'number_with_unit' — companion `work_orders` column that stores the chosen unit. */
  unitField?: string;
  /** kind === 'number_with_unit' — units offered in the per-column unit picker (the partner's enabled subset). */
  units?: readonly string[];
  /** kind === 'number_with_unit' — the unit pre-selected for the column. */
  defaultUnit?: string;
  /** kind === 'metadata' — the value type, for parsing/validation. */
  metadataType?: CustomColumnDefinitionDtoMetadataType;
  /** kind === 'metadata' && SelectOne — the allowed option values. */
  selectOptions?: readonly string[];
}

export interface ImportCatalogInput {
  containers: PartnerContainer[];
  structuredColumns: PartnerStructuredColumnDto[];
  metadataDefinitions: CustomColumnDefinitionDto[];
}

/** The field used to match an Excel row against an existing work order. */
export const IDENTITY_FIELD = 'work_order_name';

/**
 * Build the ordered list of grid columns an Excel file can be mapped onto:
 * the Work Order identity (match key), each container (incl. Product Type),
 * the enabled structured columns, and the active custom (metadata) columns.
 *
 * Ordering mirrors the planner grid (identity → containers → structured →
 * metadata) so the mapping UI reads left-to-right like the grid. Read-only /
 * derived columns (Product Code / Name, complexity, status, PIN) are
 * intentionally absent — they can't be imported.
 */
export function buildImportCatalog({
  containers,
  structuredColumns,
  metadataDefinitions,
}: ImportCatalogInput): ImportTarget[] {
  const targets: ImportTarget[] = [
    { field: IDENTITY_FIELD, label: 'Work Order', kind: 'identity' },
  ];

  for (const container of sortContainers(containers)) {
    targets.push({
      field: container.container_key,
      label: container.container_name,
      kind: 'container',
      container,
    });
  }

  // Only columns editable in at least one status can be imported. Some
  // structured columns are read-only in the Project Plan (e.g. Manufacturing
  // Sequence Number, reordered via the Production Planner) — they carry an
  // empty `editable_in_statuses` and the backend rejects any write to them.
  const enabledStructured = structuredColumns
    .filter((c) => c.is_enabled && c.editable_in_statuses.length > 0)
    .slice()
    .sort((a, b) => a.default_sort_order - b.default_sort_order);
  for (const column of enabledStructured) {
    targets.push(buildStructuredTarget(column));
  }

  const activeMetadata = metadataDefinitions
    .filter(isCustomColumnActive)
    .slice()
    .sort((a, b) => a.display_order - b.display_order);
  for (const def of activeMetadata) {
    const target: ImportTarget = {
      field: `metadata.${def.metadata_key}`,
      label: def.metadata_label,
      kind: 'metadata',
      metadataType: def.metadata_type,
    };
    if (def.metadata_type === CustomColumnDefinitionDtoMetadataType.SelectOne) {
      target.selectOptions = splitSelectOptions(def.metadata_data);
    }
    targets.push(target);
  }

  return targets;
}

function buildStructuredTarget(column: PartnerStructuredColumnDto): ImportTarget {
  const base = { field: column.work_order_field, label: column.label } as const;

  // data_type is a string-literal union on the DTO; the const enum object is
  // re-exported type-only from the barrel, so compare against the literals.
  switch (column.data_type) {
    case 'number_with_unit': {
      // Mirror buildStructuredColumns: enabled subset falls back to the full
      // registry allowed_units; default falls back to the first allowed unit.
      const units = column.enabled_units ?? column.allowed_units ?? [];
      const defaultUnit = column.default_unit ?? column.allowed_units?.[0] ?? units[0] ?? '';
      return {
        ...base,
        kind: 'number_with_unit',
        unitField:
          column.work_order_unit_field ?? column.work_order_field.replace(/_value$/, '_unit'),
        units,
        defaultUnit,
      };
    }
    case 'integer':
      return { ...base, kind: 'integer' };
    case 'date':
    case 'timestamptz':
      return { ...base, kind: 'date' };
    case 'text':
    default:
      return { ...base, kind: 'text' };
  }
}

/** Split a SelectOne definition's comma-separated `metadata_data` into trimmed, non-empty options. */
export function splitSelectOptions(metadataData: string | null | undefined): string[] {
  return (metadataData ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
